import { useState } from 'react';
import { Bookmark as BookmarkIcon, Trash2 } from 'lucide-react';
import { useBookmarkStore } from '@/state/bookmarkStore';
import { bookmarkSave } from '@/agent/tools/bookmark-save';
import { bookmarkGo } from '@/agent/tools/bookmark-go';

export function BookmarksPanel() {
  const bookmarks = useBookmarkStore((s) => s.bookmarks);
  const remove = useBookmarkStore((s) => s.remove);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await bookmarkSave.execute({ name: trimmed });
      setName('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onGo = async (id: string) => {
    setError(null);
    try {
      await bookmarkGo.execute({ id });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Bookmark name"
          className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={onSave}
          disabled={busy || !name.trim()}
          className="rounded border border-border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
        >
          Save view
        </button>
      </div>

      {error && <div className="text-xs text-destructive">{error}</div>}

      {bookmarks.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          No bookmarks yet. Pan/zoom the map and save it.
        </div>
      ) : (
        <ul className="flex flex-col gap-1">
          {bookmarks.map((b) => (
            <li
              key={b.id}
              className="flex items-center gap-2 rounded border border-border px-2 py-1 text-xs"
            >
              <button
                type="button"
                onClick={() => onGo(b.id)}
                className="flex min-w-0 flex-1 items-center gap-2 truncate text-left hover:text-foreground"
                title={`Go to ${b.name}`}
              >
                <BookmarkIcon className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{b.name}</span>
              </button>
              <button
                type="button"
                onClick={() => remove(b.id)}
                className="grid size-6 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-destructive"
                aria-label="Delete bookmark"
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
